import store from '@/store';
import { applyReplacementTags } from '@/utilities/Utilities';
import { ReplacementTags } from '@/store/modules/global/state';
import { ConfigData } from '@/Config';

export interface QueryParameters {
  [key: string]: string;
}

/**
 * Validity can be checked here: https://jsfiddle.net/f20htdjq/2/
 * @param string
 */
export const isValidHttpUrl = (string: string) => {
  // eslint-disable-next-line security/detect-non-literal-regexp
  const pattern = new RegExp(
    '^((https?:)?\\/\\/)?' + // protocol
      '(?:\\S+(?::\\S*)?@)?' + // authentication
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locater
  return pattern.test(string);
};

export function parseQueryString(query: string): QueryParameters {
  const querySplit = query.split('?');

  if (!querySplit[1]) {
    return {};
  }

  const vars = querySplit[1].split('&');
  const parameters: QueryParameters = {};

  for (let i = 0; i < vars.length; i++) {
    // eslint-disable-next-line security/detect-object-injection
    const pair = vars[i].split('=');

    parameters[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
  }

  return parameters;
}

export const formatUrl = (url?: string, extraReplacementTags?: ReplacementTags | undefined): undefined | string => {
  if (!url) {
    return url;
  }

  if (
    (!(url.indexOf(':') !== -1 && url.indexOf('http') === -1) && !(url.indexOf('coop-game-') === 0)) ||
    url.indexOf('lotto://') === 0
  ) {
    if (
      url &&
      url.indexOf('http://') === -1 &&
      url.indexOf('https://') === -1 &&
      url.indexOf(':') === -1 &&
      url[0] != '/' &&
      url[0] != '#'
    ) {
      // Can't locate either http or https protocol.
      url = 'http://' + url;
    }

    url = insertUTMInUrl(url);
    url = addTrackingUrl(url);
  }

  return applyReplacementTags(url, extraReplacementTags);
};

export function getCurrentUrl(): string {
  return typeof window !== 'undefined' ? window.location.href : store.getters['utility/path'] ?? '';
}

export function getQueryParams(): QueryParameters {
  return {
    ...parseQueryString(getCurrentUrl()),
    ...store.state.global.staticQueryParams
  };
}

export function getSpecificQueryParam(param: string) {
  const params = new URLSearchParams(window.location.href);
  return params.get(param);
}

const insertUTMInUrl = (url: string): string => {
  const campaignState = store.getters['campaign/state'];
  if (
    campaignState.config?.utmEnabled &&
    url.indexOf('http') !== -1 &&
    url.indexOf('utm_source=') === -1 &&
    url.indexOf('utm_medium=') === -1 &&
    url.indexOf('utm_campaign=') === -1 &&
    url.indexOf('utm_campaign=') === -1 &&
    typeof campaignState.config?.utmCampaign !== 'undefined' &&
    typeof campaignState.config?.utmMedium !== 'undefined' &&
    typeof campaignState.config?.platform !== 'undefined'
  ) {
    const params = getQueryParams();
    const queryArgs: string[] = [];

    const utmSource = params.utm_source || campaignState.config.platform;
    const utmMedium = params.utm_medium || campaignState.config.utmMedium;
    const utmCampaign = params.utm_campaign || campaignState.config.utmCampaign;

    queryArgs.push('utm_source=' + encodeURIComponent(utmSource));
    queryArgs.push('utm_medium=' + encodeURIComponent(utmMedium));
    queryArgs.push('utm_campaign=' + encodeURIComponent(utmCampaign));

    if (typeof params.utm_content !== 'undefined') {
      queryArgs.push('utm_content=' + encodeURIComponent(params.utm_content));
    }

    if (url.indexOf('?') !== -1) {
      url += '&';
    } else {
      url += '?';
    }

    url += queryArgs.join('&');
  }

  return url;
};

export const replaceHrefsWithUrlTrackingAndReplacementTags = (
  htmlString: string,
  extraReplacementTags?: ReplacementTags
) => {
  return applyReplacementTags(htmlString, extraReplacementTags).replace(/<a[^>]*>([^<]+)<\/a>/gm, (match) => {
    return match.replace(/<a.*href="(.+?)"/gm, (match, contents) => {
      return match.replace(contents, formatUrl(contents) ?? '');
    });
  });
};

/**
 * Get URL's tracking link.
 * @param url
 */
const addTrackingUrl = (url: string) => {
  const campaignState = store.getters['campaign/state'];

  if (url && (url.indexOf('#') === 0 || url.match(/#[a-z0-9_-]+#/gim)) && url !== '#url#') {
    return url;
  }

  if (
    url &&
    typeof campaignState !== 'undefined' &&
    typeof campaignState.config !== 'undefined' &&
    campaignState.config.deactivateInternalLinkTracking
  ) {
    return url;
  }

  if (url) {
    url = url
      .replace(/&aelig;/gi, 'æ')
      .replace(/&oslash;/gi, 'ø')
      .replace(/&aring;/gi, 'å')
      .replace(/&AElig;/gi, 'Æ')
      .replace(/&Oslash;/gi, 'Ø')
      .replace(/&Aring;/gi, 'Å')
      .replace(/&amp;/gi, '&');
  }

  const sanitizedUrl = url === '#url#' ? url : encodeURIComponent(url);

  return (
    (campaignState.config?.apiEndpoint ?? ConfigData.apiEndpoint) +
    '/track?url=' +
    sanitizedUrl +
    '&campaign_id=' +
    campaignState.id +
    '&customer_id=' +
    campaignState.config?.customerId
  );
};

export const isHostIPAddress = (host: string) => {
  return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
    host
  );
};
